import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  AsyncCampaignCard,
  AsyncStatsCard,
  AudienceTable,
  Button,
  CampaignSuggestion,
  Info,
} from '@demandlocal/components'
import { useGetTotalBudget } from '../../../../../api/dealership/profile/useGetTotalBudget'
import { formatNumber } from '../../../../../lib/utils/formatNumber'
import { useGetMainKpis } from '../../../../../api/dealership/profile/useGetMainKpis'
import { useGetImpressionStats } from '../../../../../api/dealership/profile/useGetImpressionStats'
import { useGetDealersStats } from '../../../../../api/dealership/profile/useGetDealersStats'
import { useGetPolkStats } from '../../../../../api/dealership/profile/useGetPolkStats'
import KPICard from '../../../../../components/Overview/KPICard'
import { useGetDealerProfileAudiences } from '../../../../../api/dealership/profile/useGetDealerProfileAudiences'
import { productCodeToVendorIcon } from '../../../../../lib/utils/mapVendorsToLogos'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { ADMIN_VENDOR_MAP } from '../../../../../lib/utils/vendors'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import { useGetAllCampaignsByDealership } from '../../../../../api/dealership/campaigns/useGetAllCampaignsByDealership'

const loadingCampaign = {
  client_campaign_id: 0,
  client_campaign_display_name: '',
  vendor_id: 0,
  product_code: '',
  product_name: '',
  start_date: '',
  end_date: '',
  clicks: 0,
  impressions: 0,
  views: 0,
  audience_impressions: 0,
  vehicles: 0,
  ctr: 0,
  vtr: 0,
  leads: 0,
  calls: 0,
  demand_local_net: 0,
  budget: 0,
}

export default function PolkDealerProfile(): JSX.Element {
  const navigate = useNavigate()
  const dealership = useDealership()
  const { t } = useTranslation('routes')

  const { agency_id } = useParams()
  const agency = useGetAgencyByAgencyId(agency_id)

  const loadingKpi = { tag_name: '', kpi: 0 }
  const {
    data: mainKPIs = {
      data: [loadingKpi, loadingKpi, loadingKpi],
    },
    isLoading: isLoadingMainKpis,
  } = useGetMainKpis()
  const { data: totalBudget = { data: 0, title: '' }, isLoading: isLoadingTotalBudget } =
    useGetTotalBudget({})
  const { data: dealersStats = { data: 0 }, isLoading: isLoadingDealersStats } =
    useGetDealersStats()
  const {
    data: impressionStats = { booked_impressions: 0, delivered_impressions: 0 },
    isLoading: isLoadingImpressionStats,
  } = useGetImpressionStats()
  const {
    data: polkData = { polk_sales: 0, new_cars_sold: 0, top_selling_model: '' },
    isLoading: isLoadingPolkData,
  } = useGetPolkStats()
  const {
    data: activeAudiences = { audiences: [], total_audiences: 0 },
    isLoading: isLoadingAudienceTable,
  } = useGetDealerProfileAudiences()
  const audienceTable = useMemo(() => {
    return {
      audiences: activeAudiences?.audiences?.map((audience) => {
        return {
          ...audience,
          to: t('dealership.audiences.report', {
            dealership,
            audience: {
              platform_id: ADMIN_VENDOR_MAP[audience?.vendor_id],
              id: audience?.id,
            },
            start_date: dayjs()
              .subtract(1, 'month')
              .startOf('month')
              .format('YYYY-MM-DD'),
            end_date: dayjs().format('YYYY-MM-DD'),
          }),
        }
      }),
      total_audiences: activeAudiences?.total_audiences,
    }
  }, [activeAudiences, dealership, t])

  const {
    data: campaignsList = { data: [loadingCampaign, loadingCampaign, loadingCampaign] },
    isLoading: isLoadingCampaigns,
  } = useGetAllCampaignsByDealership({
    filters: {
      startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    },
  })

  const colors = ['blue', 'green', 'orange']

  return (
    <div className="flex flex-col gap-5 w-full font-Spartan px-10">
      <div className="flex flex-row flex-nowrap gap-3 w-full">
        {mainKPIs.data.slice(0, 3).map((kpi, idx) => (
          <KPICard
            key={idx}
            title={kpi.tag_name}
            color={colors[idx]}
            content={
              <span className="w-full font-bold text-xl">
                {formatNumber(kpi?.conversions ?? 0)}
              </span>
            }
            isLoading={isLoadingMainKpis}
          />
        ))}
      </div>
      <div className="flex flex-row flex-nowrap gap-3 w-full">
        {(isLoadingTotalBudget || totalBudget?.title.length > 0) && (
          <AsyncStatsCard
            title={totalBudget.title}
            color="bg-chrysler-blue/20"
            content={
              <span className="font-bold">${formatNumber(totalBudget?.data)}</span>
            }
            isLoading={isLoadingTotalBudget}
          />
        )}
        <AsyncStatsCard
          title={'Active Campaigns'}
          color="bg-light-cyan"
          content={
            <span className="font-bold">{Number(campaignsList?.data?.length || 0)}</span>
          }
          isLoading={isLoadingCampaigns}
        />
        <AsyncStatsCard
          title={`Dealers in Group`}
          color="bg-primary-persian-blue/10"
          content={<span className="font-bold">{formatNumber(dealersStats?.data)}</span>}
          isLoading={isLoadingDealersStats}
        />
      </div>
      <div className="grid grid-cols-3 gap-5">
        <AsyncStatsCard
          title={'Booked Impressions'}
          content={
            <span className="font-bold">
              {formatNumber(impressionStats?.booked_impressions)}
            </span>
          }
          isLoading={isLoadingImpressionStats}
        />
        <AsyncStatsCard
          title={'Delivered Impressions'}
          content={
            <span className="font-bold">
              {formatNumber(impressionStats?.delivered_impressions)}
            </span>
          }
          isLoading={isLoadingImpressionStats}
        />
        <AsyncStatsCard
          title={'% Delivered'}
          content={
            <span className="font-bold">
              {formatNumber(
                Math.round(
                  impressionStats.booked_impressions === 0
                    ? 0
                    : (impressionStats.delivered_impressions /
                        impressionStats.booked_impressions) *
                        100,
                ),
              )}
              %
            </span>
          }
          isLoading={isLoadingImpressionStats}
        />
        <AsyncStatsCard
          title={'All Attributed Sales - Polk'}
          content={<span className="font-bold">{polkData?.polk_sales}</span>}
          isLoading={isLoadingPolkData}
        />
        <AsyncStatsCard
          title={"New Honda's Sold"}
          content={<span className="font-bold">{polkData?.new_cars_sold}</span>}
          isLoading={isLoadingPolkData}
        />
        <AsyncStatsCard
          title={'Top Selling Model'}
          content={<span className="font-bold">{polkData?.top_selling_model}</span>}
          isLoading={isLoadingPolkData}
        />
      </div>
      <div className="flex flex-col gap-3 pt-5">
        {!isLoadingAudienceTable && audienceTable?.total_audiences > 0 && (
          <div className="flex flex-row gap-5 items-center">
            <span className="font-bold">Top Performing Audiences</span>
            <span
              className="text-primary-persian-blue cursor-pointer font-bold text-xs cursor-pointer"
              onClick={() =>
                navigate(t('dealership.audiences.custom', { dealership }))
              }>
              See all audiences ({audienceTable?.total_audiences})
            </span>
          </div>
        )}
        <AudienceTable<{
          audience_name: string
          total_conversions: number
          impressions: number
          conversion_rate: number
        }>
          headers={[
            {
              label: (
                <div className="flex flex-row items-center gap-2">
                  <span>Total Audience Conversions</span>
                  <Info message="All defined conversions from programmatic site tracking. Based on campaign KPIs" />
                </div>
              ),
              key: 'total_conversions',
            },
            { label: 'Last 30 Days Impressions', key: 'impressions' },
            {
              label: (
                <div className="flex flex-row items-center gap-2">
                  <span>Media Conversion Rate</span>
                  <Info message="The percentage of users that engage with an ad and complete a key site action, which is calculated by dividing the total number of conversions by the number of impressions." />
                </div>
              ),
              key: 'conversion_rate',
            },
          ]}
          id_column="id"
          category_column_id="audience_name"
          to={'to'}
          data={audienceTable?.audiences}
          isLoading={isLoadingAudienceTable}
        />
      </div>
      <div className="flex flex-col gap-3 pt-5">
        <div className="flex flex-row justify-between items-center">
          <span className="font-bold">Campaigns</span>
          <Button
            variant="secondary"
            onClick={() => navigate(t('dealership.campaigns.list', { dealership }))}>
            See Campaign Level Reporting
          </Button>
        </div>
        <div className="grid grid-cols-3 w-full gap-5">
          {campaignsList?.data.slice(0, 3).map((campaign, idx) => (
            <AsyncCampaignCard
              key={idx}
              campaign={campaign}
              vendor_icon={
                <div className="flex max-w-[40px] object-contain rounded-full">
                  {productCodeToVendorIcon(campaign?.product_code)}
                </div>
              }
              isLoading={isLoadingCampaigns}
              to={t('dealership.campaigns.report', { dealership, campaign })}
              show_budget_type={agency.data?.show_budget_type}
            />
          ))}
          {!isLoadingCampaigns &&
            Array.from({ length: 3 - Number(campaignsList?.data?.length || 0) }).map(
              (_, idx) => (
                <CampaignSuggestion
                  key={`suggestion_${idx}`}
                  index={idx}
                  to={t('dealership.campaigns.create', { dealership })}
                />
              ),
            )}
        </div>
      </div>
    </div>
  )
}
